import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { MiniSparkline, TrendChart, OverlayChart, SwimlaneChart } from './marposs/SpcCharts';
import BenchSimple from './marposs/BenchSimple';

const API = '/machinesim/api/marposs';

const fmtUm = v => (v === null || v === undefined ? '—' : Number(v).toFixed(1));
const fmtMm = v => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(3)}`);

function barPos(c, value = c.value) {
  if (value === null || value === undefined || c.hi === c.lo) return null;
  const pct = ((Number(value) - Number(c.lo)) / (Number(c.hi) - Number(c.lo))) * 100;
  return Math.max(0, Math.min(100, pct));
}

function trendMeta(c) {
  const h = c.history || [];
  if (h.length < 3) return { label: 'Building history', dir: 'flat', tone: 'text-slate-500' };
  const take = h.slice(-6);
  const delta = Number(take.at(-1).value) - Number(take[0].value);
  const span = Math.max(1, Number(c.hi) - Number(c.lo));
  const frac = delta / span;
  if (Math.abs(frac) < 0.035) return { label: 'Stable', dir: 'flat', tone: 'text-emerald-300' };
  if (frac > 0) return { label: 'Trending high', dir: 'up', tone: 'text-amber-300' };
  return { label: 'Trending low', dir: 'down', tone: 'text-sky-300' };
}

function charSeverity(c, actionLimit) {
  if (c.value === null || c.value === undefined) return 'empty';
  if (c.verdict === 'Fuera') return 'bad';
  if (Math.abs(Number(c.bandFraction) || 0) >= actionLimit) return 'warn';
  return 'good';
}

function stateFor(c, actionLimit, proposalIds = new Set(), channelMap = new Map()) {
  if (proposalIds.has(c.id)) return { label: 'Awaiting approval', tone: 'text-amber-200', dot: 'bg-amber-400' };
  const channel = c.correctedBy
    ? channelMap.get(`${c.correctedBy.station}|${c.correctedBy.toolKey}|${c.correctedBy.family}`)
    : null;
  if (channel?.state === 'SETTLING') return { label: 'Settling', tone: 'text-sky-200', dot: 'bg-sky-400' };
  const severity = charSeverity(c, actionLimit);
  if (severity === 'bad') return { label: 'Out of tolerance', tone: 'text-red-200', dot: 'bg-red-400' };
  if (severity === 'warn') return { label: 'Action threshold', tone: 'text-amber-200', dot: 'bg-amber-400' };
  const trend = trendMeta(c);
  return { label: trend.label, tone: trend.tone, dot: trend.dir === 'up' ? 'bg-amber-400' : trend.dir === 'down' ? 'bg-sky-400' : 'bg-emerald-400' };
}

function ToleranceBar({ c, actionLimit, compact = false }) {
  const pos = barPos(c);
  const pct = v => ((Number(v) - Number(c.lo)) / Math.max(1e-9, Number(c.hi) - Number(c.lo))) * 100;
  const targetPos = pct(c.target);
  const loAction = pct(Number(c.target) - actionLimit * (Number(c.target) - Number(c.lo)));
  const hiAction = pct(Number(c.target) + actionLimit * (Number(c.hi) - Number(c.target)));
  const severity = charSeverity(c, actionLimit);

  return (
    <div>
      <div className={`relative w-full rounded-full bg-slate-800 ${compact ? 'h-1.5' : 'h-2.5'}`}>
        <div
          className="absolute inset-y-0 rounded-full bg-emerald-500/10"
          style={{ left: `${Math.max(0, loAction)}%`, width: `${Math.max(0, Math.min(100, hiAction)-Math.max(0, loAction))}%` }}
        />
        <div className="absolute inset-y-[-2px] w-px bg-slate-400/50" style={{ left: `${targetPos}%` }} />
        {pos !== null && (
          <div
            className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-slate-950 ${compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} ${
              severity === 'bad' ? 'bg-red-400' : severity === 'warn' ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ left: `${pos}%` }}
          />
        )}
      </div>
      {!compact && (
        <div className="mt-1 flex justify-between text-[9px] font-mono text-slate-600">
          <span>{c.lo}</span>
          <span>target {c.target}</span>
          <span>{c.hi}</span>
        </div>
      )}
    </div>
  );
}

function TargetPill({ c }) {
  if (!c.correctedBy) {
    return (
      <span className="inline-flex rounded-full border border-slate-700 bg-slate-900/60 px-2 py-0.5 text-[9px] font-bold tracking-wide text-slate-500">
        PROCESS ONLY
      </span>
    );
  }
  const x = c.correctedBy;
  return (
    <span
      className="inline-flex rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-bold tracking-wide text-amber-200"
      title={x.tooling || undefined}
    >
      {x.station} · O{x.offset} · {x.family}
    </span>
  );
}

function CharacteristicCard({ c, actionLimit, selected, compareMode, onSelect, proposalIds, channelMap, compact = false }) {
  const severity = charSeverity(c, actionLimit);
  const state = stateFor(c, actionLimit, proposalIds, channelMap);
  const border = selected
    ? 'border-cyan-400/80 ring-1 ring-cyan-400/30'
    : severity === 'bad'
      ? 'border-red-500/45'
      : severity === 'warn'
        ? 'border-amber-500/40'
        : c.correctable
          ? 'border-amber-500/15'
          : 'border-slate-800';

  return (
    <button
      type="button"
      onClick={() => onSelect(c.id)}
      className={`group relative text-left rounded-xl border bg-slate-900/55 hover:bg-slate-900/90 transition-all ${border} ${compact ? 'p-3' : 'p-4'}`}
    >
      {compareMode && (
        <span className={`absolute top-3 right-3 w-3.5 h-3.5 rounded border ${selected ? 'bg-cyan-400 border-cyan-300' : 'border-slate-600 bg-slate-950'}`} />
      )}

      <div className="flex items-start gap-3">
        <div className={`font-mono font-black tracking-tight ${compact ? 'text-xs' : 'text-sm'} text-slate-500`}>{c.id}</div>
        <div className="min-w-0 flex-1">
          <div className={`font-semibold text-slate-100 truncate ${compact ? 'text-xs' : 'text-sm'}`}>{c.name}</div>
          {!compact && <div className="text-[10px] text-slate-500 truncate">{c.hint}</div>}
        </div>
        <div className="text-right pr-4">
          <div className={`font-mono tabular-nums font-semibold ${compact ? 'text-base' : 'text-xl'} ${
            severity === 'bad' ? 'text-red-300' : severity === 'warn' ? 'text-amber-200' : 'text-slate-100'
          }`}>
            {fmtUm(c.value)}
          </div>
          <div className="text-[9px] font-mono tracking-wide text-slate-600">µm</div>
        </div>
      </div>

      <div className={compact ? 'mt-2' : 'mt-3'}>
        <ToleranceBar c={c} actionLimit={actionLimit} compact={compact} />
      </div>

      {!compact && (
        <div className={`mt-2 ${state.tone}`}>
          <MiniSparkline c={c} />
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className={`flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide ${state.tone}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${state.dot}`} />
          {state.label}
        </div>
        <TargetPill c={c} />
      </div>

      {c.scenario && (
        <div className="mt-2 text-[9px] font-mono text-fuchsia-300/80">
          SCENARIO {c.scenario.ratePerPart ? `${c.scenario.ratePerPart > 0 ? '+' : ''}${c.scenario.ratePerPart} µm/part` : 'HELD'}
          {' · '}{c.scenario.injectedUm > 0 ? '+' : ''}{c.scenario.injectedUm} µm
        </div>
      )}
    </button>
  );
}

function ProposalCard({ p, busy, onApprove, onReject }) {
  return (
    <div className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-amber-100">{p.characteristic}</div>
          <div className="mt-0.5 text-[10px] font-mono text-amber-300/70">
            {p.station} · O{p.offset} · {p.family}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg font-bold text-white">{fmtMm(p.deltaMm)}</div>
          <div className="text-[9px] uppercase tracking-wide text-slate-500">mm correction</div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-[9px] font-mono text-slate-400">
        <span>avg {fmtUm(p.smoothedUm)}</span>
        <span>err {fmtUm(p.errorUm)}</span>
        <span>{Math.round(Math.abs(p.bandFraction) * 100)}% band</span>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          disabled={busy}
          onClick={() => onApprove(p)}
          className="flex-1 rounded-lg bg-emerald-600 px-2 py-2 text-[11px] font-bold text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          Approve & write
        </button>
        <button
          disabled={busy}
          onClick={() => onReject(p)}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          Reject
        </button>
      </div>
      {p.tooling && <div className="mt-2 truncate text-[9px] text-slate-500" title={p.tooling}>{p.tooling}</div>}
    </div>
  );
}

function HudTile({ label, value, tone = 'text-slate-100', note }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2.5 min-w-[118px]">
      <div className="text-[9px] uppercase tracking-[0.14em] text-slate-600">{label}</div>
      <div className={`mt-0.5 text-sm font-bold ${tone}`}>{value}</div>
      {note && <div className="mt-0.5 text-[9px] text-slate-600">{note}</div>}
    </div>
  );
}

function processState(view, actionLimit) {
  if (!view || !(view.measurementId > 0)) {
    return { label: 'NO DATA', tone: 'text-slate-300', border: 'border-slate-700', dot: 'bg-slate-500' };
  }
  const chars = view?.characteristics || [];
  if ((view?.alerts || []).length) return { label: 'PROCESS ALERT', tone: 'text-red-200', border: 'border-red-500/40', dot: 'bg-red-400' };
  if ((view?.proposals || []).length) return { label: 'AWAITING APPROVAL', tone: 'text-amber-200', border: 'border-amber-500/40', dot: 'bg-amber-400' };
  if (chars.some(c => Math.abs(Number(c.bandFraction) || 0) >= actionLimit)) return { label: 'DRIFT WATCH', tone: 'text-amber-200', border: 'border-amber-500/30', dot: 'bg-amber-400' };
  return { label: 'IN CONTROL', tone: 'text-emerald-200', border: 'border-emerald-500/30', dot: 'bg-emerald-400' };
}

function ActionRail({ view, busy, approve, reject, actionLimit }) {
  const proposals = view?.proposals || [];
  const alerts = view?.alerts || [];
  const adjustments = Object.entries(view?.adjustments || {}).filter(([,v]) => Math.abs(Number(v) || 0) > 0.00001);
  const recent = (view?.history || []).slice(0, 6);

  return (
    <aside className="space-y-4 xl:sticky xl:top-3">
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500">Action queue</h3>
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${proposals.length ? 'bg-amber-500/15 text-amber-200' : 'bg-emerald-500/10 text-emerald-300'}`}>
            {proposals.length || 'CLEAR'}
          </span>
        </div>
        {proposals.length ? (
          <div className="mt-3 space-y-2">{proposals.map(p => <ProposalCard key={p.id} p={p} busy={busy} onApprove={approve} onReject={reject} />)}</div>
        ) : (
          <div className="mt-3 rounded-lg border border-emerald-500/10 bg-emerald-500/[0.04] p-3">
            <div className="text-xs font-semibold text-emerald-200">No offset action pending</div>
            <div className="mt-1 text-[10px] text-slate-500">Correctable trends act at {Math.round(actionLimit*100)}% of band after the configured smoothing window.</div>
          </div>
        )}
      </section>

      {alerts.length > 0 && (
        <section className="rounded-xl border border-red-500/25 bg-red-500/[0.05] p-3">
          <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-red-300/70">Process alerts</h3>
          <div className="mt-2 space-y-2">
            {alerts.map(a => (
              <div key={a.characteristicId} className="rounded-lg bg-slate-950/40 p-2">
                <div className="text-xs font-semibold text-red-200">{a.characteristicId} · {a.name}</div>
                <div className="mt-1 text-[10px] text-slate-400">{fmtUm(a.value)} µm · no legitimate offset path</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
        <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500">Channels</h3>
        <div className="mt-2 space-y-1.5">
          {(view?.channels || []).map(c => (
            <div key={c.key} className="flex items-center gap-2 rounded-lg bg-slate-950/35 px-2 py-1.5 text-[10px] font-mono">
              <span className="w-16 text-slate-300">{c.station} {c.tool}</span>
              <span className="w-5 text-slate-500">{c.family}</span>
              <span className={`rounded px-1.5 py-0.5 ${
                c.state === 'PROPOSED' ? 'bg-amber-500/15 text-amber-200'
                : c.state === 'SETTLING' ? 'bg-sky-500/15 text-sky-200'
                : c.state === 'BLOCKED' ? 'bg-red-500/15 text-red-200'
                : 'bg-slate-800 text-slate-500'
              }`}>{c.state}</span>
            </div>
          ))}
          {!view?.channels?.length && <div className="text-[10px] text-slate-600">Waiting for samples.</div>}
        </div>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
        <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500">Stored adjustments</h3>
        <div className="mt-2">
          {adjustments.length ? adjustments.map(([k,v]) => (
            <div key={k} className="flex justify-between border-b border-slate-800/60 py-1 text-[10px] font-mono">
              <span className="text-slate-500">{k.replaceAll('|',' · ')}</span>
              <span className="text-slate-200">{fmtMm(v)} mm</span>
            </div>
          )) : <div className="text-[10px] text-slate-600">No stored correction yet.</div>}
        </div>
      </section>

      {recent.length > 0 && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
          <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500">Recent decisions</h3>
          <div className="mt-2 space-y-1.5">
            {recent.map(p => (
              <div key={p.id} className="flex gap-2 text-[10px] font-mono">
                <span className="text-slate-600">{p.id}</span>
                <span className={p.state === 'APPLIED' ? 'text-emerald-300' : p.state === 'CANCELLED' ? 'text-slate-500' : 'text-slate-400'}>{p.state}</span>
                <span className="ml-auto text-slate-500">{p.station || ''} {p.offset ? `O${p.offset}` : ''} {fmtMm(p.deltaMm)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </aside>
  );
}

function TrendWorkbench({ view, selectedIds, setSelectedIds, chartMode, setChartMode, drift, setDrift, busy, setDriftFor, clearDriftFor, actionLimit }) {
  const chars = view?.characteristics || [];
  const selected = selectedIds.map(id => chars.find(c => c.id === id)).filter(Boolean);
  const primary = selected[0];

  if (!primary) {
    return (
      <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/25 p-6 text-center text-xs text-slate-500">
        Select a characteristic to inspect its cycle history.
      </div>
    );
  }

  const toolLifeKey = primary.correctedBy ? `${primary.correctedBy.station}|${primary.correctedBy.toolKey}` : null;
  const toolLife = toolLifeKey ? (view.toolLife || []).find(t => t.key === toolLifeKey) : null;

  return (
    <section className="rounded-xl border border-cyan-500/15 bg-slate-950/45 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-4 py-3">
        <div className="mr-auto">
          <div className="text-[9px] font-black uppercase tracking-[0.16em] text-cyan-300/70">Trend workbench</div>
          <div className="mt-0.5 text-sm font-semibold text-slate-100">
            {selected.length === 1 ? `${primary.id} · ${primary.name}` : `${selected.length} characteristics selected`}
          </div>
        </div>
        {['trend','overlay','swimlanes'].map(mode => (
          <button
            key={mode}
            onClick={() => setChartMode(mode)}
            disabled={mode === 'overlay' && (selected.length < 2 || selected.length > 7)}
            className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide disabled:opacity-30 ${
              chartMode === mode ? 'border-cyan-400/50 bg-cyan-400/10 text-cyan-200' : 'border-slate-700 bg-slate-900 text-slate-400 hover:text-slate-200'
            }`}
          >
            {mode}
          </button>
        ))}
        <button
          onClick={() => { setSelectedIds(chars.map(c => c.id)); setChartMode('swimlanes'); }}
          className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-slate-200"
        >
          All 19
        </button>
      </div>

      <div className={`p-3 ${chartMode === 'swimlanes' && selected.length > 8 ? 'max-h-[720px] overflow-y-auto' : ''}`}>
        {chartMode === 'trend' && <TrendChart characteristic={primary} actionLimit={actionLimit} events={view.history || []} />}
        {chartMode === 'overlay' && <OverlayChart characteristics={selected} actionLimit={actionLimit} />}
        {chartMode === 'swimlanes' && <SwimlaneChart characteristics={selected} actionLimit={actionLimit} />}
      </div>

      <div className="grid gap-3 border-t border-slate-800 p-4 lg:grid-cols-[1fr_1fr]">
        <div>
          <div className="flex flex-wrap gap-2">
            {selected.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedIds(ids => ids.filter(id => id !== c.id))}
                className="rounded-full border border-slate-700 bg-slate-900 px-2 py-1 text-[10px] text-slate-300 hover:border-slate-500"
              >
                {c.id} ×
              </button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
            <div className="rounded-lg bg-slate-900/60 p-2">
              <div className="text-slate-600">Current</div>
              <div className="mt-0.5 font-mono text-sm text-slate-100">{fmtUm(primary.value)} µm</div>
            </div>
            <div className="rounded-lg bg-slate-900/60 p-2">
              <div className="text-slate-600">Tolerance</div>
              <div className="mt-0.5 font-mono text-sm text-slate-100">{primary.lo} … {primary.hi}</div>
            </div>
            {primary.correctedBy && (
              <>
                <div className="rounded-lg bg-amber-500/[0.05] p-2">
                  <div className="text-slate-600">Correction path</div>
                  <div className="mt-0.5 text-xs font-semibold text-amber-200">{primary.correctedBy.station} → O{primary.correctedBy.offset} → {primary.correctedBy.family}</div>
                </div>
                <div className="rounded-lg bg-slate-900/60 p-2">
                  <div className="text-slate-600">Tool life</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-200">{toolLife ? `${toolLife.count} / ${toolLife.preset}` : '—'}</div>
                </div>
              </>
            )}
          </div>
          {primary.correctedBy?.tooling && <div className="mt-2 text-[10px] text-slate-500">{primary.correctedBy.tooling}</div>}
        </div>

        <div className="rounded-xl border border-fuchsia-500/15 bg-fuchsia-500/[0.035] p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] font-black uppercase tracking-[0.15em] text-fuchsia-300/70">Scenario lab</div>
              <div className="text-[10px] text-slate-500">Synthetic drift for demo/testing only</div>
            </div>
            {primary.scenario && <span className="rounded-full bg-fuchsia-500/10 px-2 py-0.5 text-[9px] font-bold text-fuchsia-200">ACTIVE</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              type="number"
              step="0.5"
              value={drift[primary.id] ?? (primary.scenario?.ratePerPart ?? '')}
              onChange={e => setDrift(d => ({ ...d, [primary.id]: e.target.value }))}
              placeholder="µm / part"
              className="min-w-[110px] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs font-mono text-slate-200 outline-none focus:border-fuchsia-400/50"
            />
            <button
              disabled={busy}
              onClick={() => setDriftFor(primary.id, Number(drift[primary.id] ?? primary.scenario?.ratePerPart ?? 0))}
              className="rounded-lg bg-fuchsia-700/70 px-3 py-2 text-[10px] font-bold text-white hover:bg-fuchsia-600 disabled:opacity-40"
            >
              Start drift
            </button>
            {primary.scenario && (
              <>
                <button
                  disabled={busy || !primary.scenario.ratePerPart}
                  onClick={() => { setDrift(d => ({ ...d, [primary.id]: '0' })); setDriftFor(primary.id, 0); }}
                  className="rounded-lg border border-slate-700 px-3 py-2 text-[10px] font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-30"
                >
                  Hold
                </button>
                <button
                  disabled={busy}
                  onClick={() => { setDrift(d => ({ ...d, [primary.id]: '' })); clearDriftFor(primary.id); }}
                  className="rounded-lg border border-slate-700 px-3 py-2 text-[10px] font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-30"
                >
                  Clear
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MarpossBench({ machineName, onClose, embedded = false }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [drift, setDrift] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [chartMode, setChartMode] = useState('trend');
  const [diagnosticsCollapsed, setDiagnosticsCollapsed] = useState(false);
  // Operator mode is a PROPERTY OF THE STATION, not of the session: the bench tablet on the floor
  // should come back up in Spanish after a reload without anyone re-choosing it, while the engineer
  // opening the same URL on a laptop keeps the detailed view they left. localStorage is per-browser,
  // which is exactly that distinction. Reads are guarded because a kiosk browser can refuse it.
  const [simple, setSimple] = useState(() => {
    try { return localStorage.getItem('marposs.view') === 'simple'; } catch { return false; }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pollRef = useRef(null);
  const workbenchRef = useRef(null);
  const shellRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${encodeURIComponent(machineName)}`);
      setView(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [machineName]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 2000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem('marposs.view', simple ? 'simple' : 'detail'); } catch { /* kiosk */ }
  }, [simple]);

  // Track fullscreen from the DOM rather than from our own click, so pressing Esc — which the browser
  // handles without telling us — cannot leave the button lying about which state the screen is in.
  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) { document.exitFullscreen?.(); return; }
    shellRef.current?.requestFullscreen?.().catch(() => { /* denied by policy — stay windowed */ });
  };

  useEffect(() => {
    if (!view?.characteristics?.length || selectedIds.length) return;
    const proposalId = view.proposals?.[0]?.characteristicId;
    const attention = view.characteristics.find(c => Math.abs(Number(c.bandFraction) || 0) >= (view.tuning?.actionLimitFrac ?? 0.6))?.id;
    setSelectedIds([proposalId || attention || view.characteristics[0].id]);
  }, [view, selectedIds.length]);

  const act = async fn => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); await load(); }
  };

  const measure = count => act(async () => {
    const { data } = await axios.post(`${API}/${encodeURIComponent(machineName)}/measure`, { count });
    setView(data.view);
    setNotice(`Measured ${count} part${count > 1 ? 's' : ''}.`);
  });

  const setDriftFor = (id, rate) => act(async () => {
    const { data } = await axios.post(`${API}/${encodeURIComponent(machineName)}/scenario`, { characteristicId: id, ratePerPart: rate });
    setView(data.view);
    setNotice(rate ? `${id} drifting ${rate > 0 ? '+' : ''}${rate} µm/part.` : `${id} drift held.`);
  });

  const clearDriftFor = id => act(async () => {
    const { data } = await axios.delete(`${API}/${encodeURIComponent(machineName)}/scenario/${id}`);
    setView(data.view);
    setNotice(`${id} scenario cleared.`);
  });

  const approve = p => act(async () => {
    try {
      const { data } = await axios.post(`${API}/proposals/${p.id}/approve`);
      if (data.view) setView(data.view);
      setNotice(data.ok
        ? `${p.station} O${p.offset} ${p.family}: wrote ${fmtMm(p.deltaMm)} mm, read back ${fmtMm(data.readBackAdjustmentMm)} mm.`
        : `Refused: ${data.reason}`);
    } catch (err) {
      const d = err.response?.data;
      if (d?.view) setView(d.view);
      setNotice(`Refused: ${d?.reason || d?.error || err.message}`);
    }
  });

  const reject = p => act(async () => {
    const { data } = await axios.post(`${API}/proposals/${p.id}/reject`, { reason: 'rejected on the SPC screen' });
    if (data.view) setView(data.view);
    setNotice(`${p.id} rejected.`);
  });

  const reset = () => act(async () => {
    const { data } = await axios.post(`${API}/${encodeURIComponent(machineName)}/reset`);
    setView(data.view);
    setNotice('Bench history reset. Stored offset corrections are unchanged.');
  });

  const actionLimit = view?.tuning?.actionLimitFrac ?? 0.6;
  const chars = view?.characteristics || [];
  const correctable = chars.filter(c => c.correctable);
  const diagnostic = chars.filter(c => !c.correctable);
  const attention = chars.filter(c => charSeverity(c, actionLimit) === 'warn' || charSeverity(c, actionLimit) === 'bad');
  const ps = processState(view, actionLimit);
  const proposalIds = useMemo(() => new Set((view?.proposals || []).map(p => p.characteristicId)), [view?.proposals]);
  const channelMap = useMemo(
    () => new Map((view?.channels || []).map(c => [`${c.station}|${c.tool}|${c.family}`, c])),
    [view?.channels]
  );

  const revealWorkbench = () => {
    // The explicit card click should have an obvious spatial result. The overview remains fully
    // expanded on first load; only a user selection collapses the 16-card diagnostic block.
    setDiagnosticsCollapsed(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        workbenchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const selectChar = id => {
    if (!compareMode) {
      setSelectedIds([id]);
      setChartMode('trend');
      revealWorkbench();
      return;
    }
    setSelectedIds(ids => {
      if (ids.includes(id)) return ids.length === 1 ? ids : ids.filter(x => x !== id);
      const next = [...ids, id];
      if (next.length > 1 && chartMode === 'trend') setChartMode('overlay');
      return next;
    });
    revealWorkbench();
  };

  const body = (
    <div
      ref={shellRef}
      className={`flex flex-col overflow-hidden border border-slate-800 bg-[#08111f] shadow-2xl ${
        isFullscreen
          ? 'h-screen w-screen rounded-none'
          : `rounded-2xl ${embedded ? 'w-full min-h-[calc(100vh-9rem)]' : 'w-full max-w-[1550px] max-h-[94vh]'}`
      }`}
    >
      <header className="shrink-0 border-b border-slate-800 bg-slate-950/45 px-5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-100">
                {simple ? 'Banco de Calidad' : 'SPC Quality Station'}
              </h2>
              <span className="text-slate-600">— {view?.bench?.station || '…'}</span>
            </div>
            <div className="mt-0.5 truncate text-[10px] font-mono text-slate-500">
              {view?.bench?.gauge} · {view?.bench?.partProgram} · CNC {view?.bench?.cncProgram} · part {view?.bench?.partNumber}
            </div>
          </div>

          {/* Operator mode carries the same status as a full-width Spanish banner in the body. Keeping
              this pill too would put the state on screen twice, in two languages. */}
          {!simple && (
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${ps.border} ${ps.tone}`}>
              <span className={`h-2 w-2 rounded-full ${ps.dot}`} />
              <span className="text-[10px] font-black tracking-[0.12em]">{ps.label}</span>
            </div>
          )}

          {/* The two measure controls live in the body in operator mode, next to the status the
              operator is reading, so the header does not carry two ways to do the same thing. */}
          {!simple && (
            <>
              <button onClick={() => measure(1)} disabled={busy} className="rounded-lg bg-slate-700 px-3 py-2 text-xs font-bold text-slate-100 hover:bg-slate-600 disabled:opacity-40">Measure part</button>
              <button onClick={() => measure(10)} disabled={busy} className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 disabled:opacity-40">×10</button>
              <button onClick={reset} disabled={busy} className="rounded-lg border border-slate-800 px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-300 disabled:opacity-40">Reset</button>
            </>
          )}

          <div className="inline-flex overflow-hidden rounded-lg border border-slate-700">
            <button
              onClick={() => setSimple(true)}
              className={`px-3 py-2 text-xs font-bold ${simple ? 'bg-cyan-500/20 text-cyan-200' : 'bg-slate-900 text-slate-500 hover:text-slate-300'}`}
            >
              Operador
            </button>
            <button
              onClick={() => setSimple(false)}
              className={`px-3 py-2 text-xs font-bold ${simple ? 'bg-slate-900 text-slate-500 hover:text-slate-300' : 'bg-cyan-500/20 text-cyan-200'}`}
            >
              Ingeniería
            </button>
          </div>

          <button
            onClick={toggleFullscreen}
            className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-bold text-slate-400 hover:text-slate-100"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFullscreen ? '⤡' : '⤢'}
          </button>
          {!embedded && onClose && (
            <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-800 hover:text-white" aria-label="Close SPC panel">×</button>
          )}
        </div>

        {!simple && (
        <div className="mt-3 flex flex-wrap gap-2">
          <HudTile label="Measurement" value={`#${view?.measurementId ?? 0}`} note={`part ${view?.partCountAtMeasure ?? '—'}`} />
          <HudTile label="Sampling" value={view?.sampleEveryParts === 1 ? '100%' : `1 in ${view?.sampleEveryParts ?? '—'}`} note="inspection bench" />
          <HudTile label="Attention" value={attention.length} tone={attention.length ? 'text-amber-200' : 'text-emerald-200'} note="of 19 characteristics" />
          <HudTile label="Offset actions" value={view?.proposals?.length ?? 0} tone={view?.proposals?.length ? 'text-amber-200' : 'text-emerald-200'} note="awaiting approval" />
          <HudTile label="Correctable" value="3 / 19" tone="text-amber-100" note="rest are process-only" />
          {notice && <div className="ml-auto self-center text-[10px] font-mono text-slate-400">{notice}</div>}
        </div>
        )}
      </header>

      {error && <div className="shrink-0 border-b border-red-900/40 bg-red-950/40 px-5 py-2 text-xs text-red-200">{error}</div>}

      {simple ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <BenchSimple
            view={view}
            actionLimit={actionLimit}
            estado={ps.label}
            busy={busy}
            notice={notice}
            onMedir={measure}
            onAprobar={approve}
            onRechazar={reject}
          />
        </div>
      ) : (
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <main className="min-w-0 space-y-4">
            <section className="rounded-xl border border-amber-500/10 bg-amber-500/[0.025] p-3">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <div className="mr-auto">
                  <div className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-300/70">Offset-correctable</div>
                  <div className="mt-0.5 text-[10px] text-slate-500">Three dimensions have a declared upstream wear-offset path.</div>
                </div>
                <button
                  onClick={() => setCompareMode(v => !v)}
                  className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide ${compareMode ? 'border-cyan-400/50 bg-cyan-400/10 text-cyan-200' : 'border-slate-700 bg-slate-900 text-slate-400'}`}
                >
                  {compareMode ? 'Compare on' : 'Compare'}
                </button>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {correctable.map(c => (
                  <CharacteristicCard key={c.id} c={c} actionLimit={actionLimit} selected={selectedIds.includes(c.id)} compareMode={compareMode} onSelect={selectChar} proposalIds={proposalIds} channelMap={channelMap} />
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/25 p-3">
              <div className={diagnosticsCollapsed ? '' : 'mb-3'}>
                <div className="flex items-center gap-2">
                  <div className="mr-auto">
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Process diagnostics · {diagnostic.length}</div>
                    {!diagnosticsCollapsed && (
                      <div className="mt-0.5 text-[10px] text-slate-600">Form, runout and variation are visible here, but no wear-offset write is offered.</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setDiagnosticsCollapsed(v => !v)}
                    className="rounded-lg border border-slate-700 bg-slate-950/40 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wide text-slate-500 hover:text-slate-200"
                  >
                    {diagnosticsCollapsed ? 'Show 16' : 'Collapse'}
                  </button>
                </div>
                {diagnosticsCollapsed && (
                  <div className="mt-1 text-[10px] text-slate-600">
                    Collapsed while inspecting {selectedIds.length === 1 ? selectedIds[0] : `${selectedIds.length} characteristics`} · click Show 16 to restore overview.
                  </div>
                )}
              </div>
              {!diagnosticsCollapsed && (
                <div className="grid gap-2 md:grid-cols-2 2xl:grid-cols-3">
                  {diagnostic.map(c => (
                    <CharacteristicCard key={c.id} c={c} actionLimit={actionLimit} selected={selectedIds.includes(c.id)} compareMode={compareMode} onSelect={selectChar} proposalIds={proposalIds} channelMap={channelMap} compact />
                  ))}
                </div>
              )}
            </section>

            <div ref={workbenchRef} className="scroll-mt-4">
            <TrendWorkbench
              view={view || {}}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              chartMode={chartMode}
              setChartMode={setChartMode}
              drift={drift}
              setDrift={setDrift}
              busy={busy}
              setDriftFor={setDriftFor}
              clearDriftFor={clearDriftFor}
              actionLimit={actionLimit}
            />
            </div>
          </main>

          <ActionRail view={view || {}} busy={busy} approve={approve} reject={reject} actionLimit={actionLimit} />
        </div>
      </div>
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      {body}
    </div>
  );
}

export default MarpossBench;
